import { useListClinics } from '@pa/api/admin'
import { FilterMenu, Pager } from '@pa/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { Building2, Filter, Plus, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ActAsClinicAdmin } from '@/features/impersonation'
import { listParams, type ClinicsSearch } from '../model/search'
import { HealthCell } from './HealthCell'
import { StatusPill } from './StatusPill'
import { TestingBadge } from './TestingBadge'

const STATUS_OPTIONS = [
  { value: 'healthy', label: 'Healthy' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'inactive', label: 'Inactive' },
]

const created = (iso: string) => new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' })

/**
 * S-02 Clinics (UserFlows S-02, Frontend §7.2): search, status filter (All / Healthy / Needs attention / Inactive,
 * #38 `status`), numbered pager, empty state. Columns: Name + ID, Status, Health (task 14), Bookings (7 days), Errors
 * (24 h), Users, Created; task 13 adds the row's "Act as clinic admin" (S-06).
 */
export function ClinicsPage({ search }: { search: ClinicsSearch }) {
  const navigate = useNavigate()
  const [q, setQ] = useState(search.q ?? '')
  const query = useListClinics(listParams(search), { query: { staleTime: 30_000 } })
  const setSearch = (next: ClinicsSearch) => void navigate({ to: '/clinics', search: next, replace: true })

  // Typing updates the URL with replace, debounced by 300 ms; any filter change resets the page (Frontend §6.1).
  useEffect(() => {
    const trimmed = q.trim()
    if (trimmed === (search.q ?? '')) return
    const t = setTimeout(() => {
      void navigate({
        to: '/clinics',
        search: { ...search, q: trimmed || undefined, page: undefined },
        replace: true,
      })
    }, 300)
    return () => {
      clearTimeout(t)
    }
  }, [q, search, navigate])

  const data = query.data
  const filtered = Boolean(search.q) || (search.status !== undefined && search.status !== 'all')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Clinics</h1>
          <p>Every clinic on the platform.</p>
        </div>
        <div className="actions">
          <Link to="/clinics/new" className="btn btn-primary">
            <Plus aria-hidden /> Add clinic
          </Link>
        </div>
      </div>
      <div className="card">
        <div className="toolbar filters">
          <div className="search input-wrap">
            <Search aria-hidden />
            <input
              className="input"
              type="search"
              aria-label="Search clinics"
              placeholder="Search by name or Clinic ID"
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
              }}
            />
          </div>
          <FilterMenu
            label="Status"
            icon={<Filter aria-hidden />}
            value={search.status && search.status !== 'all' ? search.status : null}
            options={STATUS_OPTIONS}
            allLabel="All"
            onChange={(value) => {
              setSearch({
                ...search,
                status: (value ?? undefined) as ClinicsSearch['status'],
                page: undefined,
              })
            }}
          />
        </div>
        {query.isPending ? (
          <div className="empty" role="status">
            Loading clinics…
          </div>
        ) : query.isError ? (
          <div className="empty" role="alert">
            <div>Couldn&apos;t load the clinics.</div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void query.refetch()}>
              Retry
            </button>
          </div>
        ) : !data ? null : data.total === 0 && !filtered ? (
          <div className="empty">
            <Building2 aria-hidden />
            <div>No clinics yet</div>
            <Link to="/clinics/new" className="btn btn-primary btn-sm">
              <Plus aria-hidden /> Add clinic
            </Link>
          </div>
        ) : data.items.length === 0 ? (
          <div className="empty">
            <Search aria-hidden />
            <div>No clinics match these filters</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table rtable clinics-table">
              <thead>
                <tr>
                  <th>Clinic</th>
                  <th>Status</th>
                  <th>Health</th>
                  <th>Bookings (7 days)</th>
                  <th>Errors (24 h)</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id}>
                    <td className="c-patient">
                      <div className="cell-person">
                        <div>
                          <Link to="/clinics/$clinicId/$tab" params={{ clinicId: c.id, tab: 'summary' }}>
                            <b>{c.name}</b>
                          </Link>
                          <span className="mono">{c.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td className="c-status" data-label="Status">
                      {c.testingIgnorePfHours ? (
                        <div className="row tag-row">
                          <StatusPill status={c.displayStatus} />
                          <TestingBadge on />
                        </div>
                      ) : (
                        <StatusPill status={c.displayStatus} />
                      )}
                    </td>
                    <td className="c-health" data-label="Health">
                      <HealthCell pf={c.pf} />
                    </td>
                    <td className="c-bookings" data-label="Bookings (7 days)">
                      {c.bookings7d}
                    </td>
                    <td className="c-errors" data-label="Errors (24 h)">
                      {c.errors24h}
                    </td>
                    <td className="c-users" data-label="Users">
                      {c.usersCount}
                    </td>
                    <td className="muted c-created" data-label="Created">
                      {created(c.createdAt)}
                    </td>
                    <td className="c-actions">
                      {/* S-06 from an S-02 row (UserFlows S-06 step 1). */}
                      <ActAsClinicAdmin clinic={c} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <div className="card-foot">
            <Pager
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              noun="clinics · by name"
              onPage={(page) => {
                setSearch({ ...search, page: page > 1 ? page : undefined })
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}
